/**
 * One World: its header, its tabs, and a watch that keeps them current.
 *
 * Every tab reads what it shows from the API when it opens and again when
 * the World tells of a commit. Nothing is cached across tabs, so what the
 * console shows is what the World says now.
 */

import type { Api, Ask, At, Commit, Holding, Occupier, Verdict, World } from './api';
import { Notices, field, h, mount, short, standingBadge } from './dom';
import { atLabel, atOf, cellsOf, clickAct, maskSlots, parseSlots, rowsFor, runs, stepDays, today, addDays, type Cell } from './grid';
import { follow, type Mode } from './live';
import { renderRelays } from './relays';
import type { LensView } from './lens-island/view';
import { loadZone, occurrenceLabel, saveZone, viewerZone, zoneOptions } from './zones';

/** Whether this build offers the Lens tab (scripts/build.ts: CONSOLE_LENS, on in every deploy since 2026-10-04). */
declare const CONSOLE_LENS: boolean | undefined;
export const LENS_TAB = typeof CONSOLE_LENS === 'boolean' && CONSOLE_LENS;

const TABS = [
  ...(LENS_TAB ? [['lens', 'Lens'] as const] : []),
  ['grid', 'Day grid'], ['occupiers', 'Occupiers'], ['journal', 'Journal'], ['relays', 'Relays'], ['asks', 'Asks'],
] as const;

/** The tab a World opens on: the lens where the build offers it, else the day grid. */
export const DEFAULT_TAB = LENS_TAB ? 'lens' : 'grid';

/** The presets the lens reads; any other World stays on the day grid. */
export const lensPresetOf = (preset: string): 'temporal-hour' | 'temporal-minute' | null =>
  preset === 'temporal-hour' || preset === 'temporal-minute' ? preset : null;

/** Per World, what the grid was last set to, so a tab switch or a refresh keeps it. */
const gridState = new Map<string, { actor: string | null; from: string; soft: boolean; softMinutes: number; explain: boolean }>();

/** Draw a World into `root`; returns what stops its watch. */
export const renderWorld = (root: HTMLElement, api: Api, name: string, tab: string, relay?: string): (() => void) => {
  const notices = new Notices();
  const head = h('div', { class: 'world-head' });
  const pill = h('span', { class: 'pill connecting', title: 'how this page hears of changes' }, 'connecting');
  const heard = h('span', { class: 'heard mono muted' });
  const body = h('div', { class: 'tab-body' });
  let world: World | null = null;
  /** What a tab leaves running (the lens), stopped with the page. */
  const disposers: Array<() => void> = [];

  const tabs = h('nav', { class: 'tabs', role: 'tablist' }, ...TABS.map(([id, label]) =>
    h('a', { href: `#/w/${encodeURIComponent(name)}/${id}`, role: 'tab', class: id === tab ? 'active' : '', 'aria-selected': String(id === tab) }, label)));

  mount(root, h('section', {},
    h('div', { class: 'page-head' },
      h('h2', {}, h('a', { href: '#/worlds', class: 'muted' }, 'Worlds'), ' / ', name),
      h('div', { class: 'live' }, pill, heard)),
    head, tabs, notices.el, body));

  const drawHead = async () => {
    const answer = await api.world(name);
    if (!answer.ok) {
      notices.show(answer.problem);
      return;
    }
    world = answer.value;
    mount(head,
      h('dl', { class: 'facts' },
        ...[
          ['global name', world.globalName], ['preset', world.preset], ['seats', world.seats],
          ['displace', world.allowDisplace ? `yes (${world.tieBreak})` : 'no'], ['occupiers', world.occupiers],
          ['relays', world.relays], ['commits', world.seq], ['head', short(world.head, 12)],
        ].flatMap(([k, v]) => [h('dt', {}, String(k)), h('dd', { class: 'mono' }, String(v))])));
  };

  let refreshTab: () => Promise<void> = async () => {};
  const refresh = async () => {
    await drawHead();
    await refreshTab();
  };

  // Several events arrive per act (commit, then its typed event); read once for all of them.
  let pending: ReturnType<typeof setTimeout> | null = null;
  const soon = () => {
    if (pending) clearTimeout(pending);
    pending = setTimeout(() => { pending = null; void refresh(); }, 150);
  };

  const stop = follow(api, name, {
    onEvent: (event) => {
      heard.textContent = `${event.type}${event.head ? ` · ${short(event.head)}` : ''}`;
      soon();
    },
    onMode: (mode: Mode, why?: string) => {
      pill.className = `pill ${mode}`;
      pill.textContent = mode;
      pill.title = why ? `${mode}: ${why}` : mode === 'live' ? 'a watch socket, opened with a ticket' : mode;
    },
  });

  void (async () => {
    await drawHead();
    if (!world) return;
    const w = world;
    switch (tab) {
      case 'occupiers': refreshTab = occupiersTab(body, api, w, notices, refresh); break;
      case 'journal': refreshTab = journalTab(body, api, w, notices); break;
      case 'relays': refreshTab = renderRelays(body, api, w, notices, relay); break;
      case 'asks': refreshTab = asksTab(body, api, w, notices); break;
      case 'lens': if (LENS_TAB) { refreshTab = lensTab(body, api, w, notices, (d) => disposers.push(d)); break; }
      // falls through: a build without the lens shows the day grid
      default: refreshTab = gridTab(body, api, w, notices, refresh);
    }
    await refreshTab();
  })();

  return () => {
    if (pending) clearTimeout(pending);
    for (const d of disposers.splice(0)) d();
    stop();
  };
};

// -- the lens -----------------------------------------------------------------------------------

/** Who to act as: the remembered choice if it is still here, else the first occupier that is not the World itself. */
const pickActing = (local: readonly Occupier[], remembered: number | null): number | null =>
  remembered !== null && local.some((o) => o.id === remembered)
    ? remembered
    : local.find((o) => !o.keeper)?.id ?? local[0]?.id ?? null;

/** Per World, the occupier the lens last acted as, for this tab. */
const lensActing = new Map<string, number | null>();

/**
 * The Fractal Lens over this World (src/lens-island): travel from the year to
 * the hour (or the minute), drag to claim, release what is yours, stamp a
 * pattern. Every write goes through the door as the occupier chosen here; the
 * lens shows it as asked until the World answers, and a refusal comes back
 * here in the API's words. Its code (React with it) loads only when this tab opens.
 */
const lensTab = (el: HTMLElement, api: Api, world: World, notices: Notices, onDispose: (d: () => void) => void) => {
  const preset = lensPresetOf(world.preset);
  if (!preset) {
    mount(el, h('p', { class: 'hint' }, `The lens reads temporal-hour and temporal-minute Worlds; this one is ${world.preset}. `,
      h('a', { href: `#/w/${encodeURIComponent(world.name)}/grid` }, 'Open the day grid')));
    return async () => {};
  }
  const actor = h('select', { 'aria-label': 'acting as' });
  let zone = loadZone();
  const zonePicker = h('select', { 'aria-label': 'time zone' },
    ...zoneOptions(viewerZone()).map((z) => h('option', { value: z, selected: z === zone }, z)));
  const zoneHint = h('span', {}, `Times are in ${zone}.`);
  const host = h('div', { class: 'lens-host console-lens' }, h('p', { class: 'hint' }, 'Loading the lens…'));
  const names = new Map<number, string>();
  let acting = lensActing.get(world.name) ?? null;
  let view: LensView | null = null;
  let opening = false;
  let gone = false;
  onDispose(() => {
    gone = true;
    view?.unmount();
    view = null;
  });

  actor.addEventListener('change', () => {
    acting = actor.value === '' ? null : Number(actor.value);
    lensActing.set(world.name, acting);
    view?.setActing(acting);
  });

  zonePicker.addEventListener('change', () => {
    zone = zonePicker.value;
    saveZone(zone);
    zoneHint.textContent = `Times are in ${zone}.`;
    view?.setZone(zone);
  });

  mount(el,
    h('div', { class: 'toolbar' },
      h('label', { class: 'inline' }, h('span', {}, 'Acting as'), actor),
      h('label', { class: 'inline' }, h('span', {}, 'Time zone'), zonePicker)),
    h('p', { class: 'hint' },
      'Pinch, ctrl/⌘ + scroll or ↓ ↑ to move between year, month, day and hour (a plain scroll moves the page); drag across slots to claim them, or switch to Release to give back yours. ',
      'A write shows as asked until the World answers; a refusal is explained above. With no occupier the lens only reads. ',
      'At a clock change, an hour the zone skips is greyed out, and one it repeats asks which you mean. ', zoneHint),
    host);

  const open = async () => {
    opening = true;
    try {
      const { openLens, linkSheet } = await import('./lens-island/view');
      if (gone) return;
      linkSheet();
      host.replaceChildren();
      view = await openLens(host, {
        api, world: world.name, preset, acting, zone,
        nameOf: (id) => names.get(id) ?? String(id),
        onProblem: (p) => notices.show(p),
        onDone: (line) => notices.ok(line),
        // A time the zone repeats or skips: the API names both UTC candidates; each becomes a button.
        onAmbiguous: (p, retry) => notices.show(p, (p.candidates ?? []).map((c) => ({
          label: occurrenceLabel(c),
          act: () => { notices.clear(); void retry(c.occurrence); },
        }))),
      });
      if (gone) view.unmount();
    } catch (error) {
      mount(host, h('p', { class: 'hint' }, 'The lens could not be loaded. ', h('a', { href: `#/w/${encodeURIComponent(world.name)}/grid` }, 'Use the day grid')));
      notices.show({ type: 'about:blank', title: 'Lens unavailable', status: 0, detail: String(error) });
    } finally {
      opening = false;
    }
  };

  return async () => {
    const answer = notices.take(await api.occupiers(world.name));
    if (!answer) return;
    // Copies of other Worlds (hands) are not held here; they are only read.
    const local = answer.occupiers.filter((o: Occupier) => o.kind !== 'foreign');
    names.clear();
    for (const o of local) names.set(o.id, o.name);
    acting = pickActing(local, acting);
    lensActing.set(world.name, acting);
    mount(actor, ...local.map((o) => h('option', { value: String(o.id), selected: o.id === acting }, `${o.name}${o.keeper ? ' (the World)' : ''} · ${o.kind}`)));
    if (view) {
      view.setActing(acting);
      await view.refresh();
    } else if (!opening) {
      await open();
    }
  };
};

// -- the day grid ---------------------------------------------------------------------------

const gridTab = (el: HTMLElement, api: Api, world: World, notices: Notices, refresh: () => Promise<void>) => {
  const state = gridState.get(world.name) ?? { actor: null, from: today(), soft: false, softMinutes: 30, explain: false };
  gridState.set(world.name, state);

  const actor = h('select', { 'aria-label': 'acting as' });
  const from = h('input', { type: 'date', value: state.from });
  const soft = h('input', { type: 'checkbox', checked: state.soft });
  const softMinutes = h('input', { type: 'number', min: 1, value: state.softMinutes, class: 'short' });
  const explain = h('input', { type: 'checkbox', checked: state.explain });
  const table = h('div', { class: 'grid-wrap' });
  const step = stepDays(world.preset);
  const shift = (days: number) => { state.from = addDays(state.from, days); from.value = state.from; void draw(); };

  actor.addEventListener('change', () => { state.actor = actor.value || null; void draw(); });
  from.addEventListener('change', () => { if (from.value) { state.from = from.value; void draw(); } });
  /** Who the cells on screen were read for: a click acts as them, never as a newer choice the grid has not caught up with. */
  let drawnFor: string | null = null;
  soft.addEventListener('change', () => { state.soft = soft.checked; });
  softMinutes.addEventListener('change', () => { state.softMinutes = Math.max(1, Number(softMinutes.value) || 30); });
  explain.addEventListener('change', () => { state.explain = explain.checked; });

  mount(el,
    h('div', { class: 'toolbar' },
      h('label', { class: 'inline' }, h('span', {}, 'Acting as'), actor),
      h('div', { class: 'inline' },
        h('button', { class: 'small', onclick: () => shift(-step), 'aria-label': 'earlier' }, '←'),
        from,
        h('button', { class: 'small', onclick: () => shift(step), 'aria-label': 'later' }, '→'),
        h('button', { class: 'small ghost', onclick: () => { state.from = today(); from.value = state.from; void draw(); } }, 'Today')),
      h('label', { class: 'inline check' }, soft, h('span', {}, 'soft, for'), softMinutes, h('span', {}, 'min')),
      h('label', { class: 'inline check' }, explain, h('span', {}, 'explain only (no write)'))),
    h('p', { class: 'hint' }, 'Click a slot to claim it; click one of yours to release it; click a soft one of yours to confirm it, or shift-click to give it up. Times are UTC.'),
    table,
    h('div', { class: 'legend' },
      ...(['free', 'mine', 'mine-soft', 'shared', 'soft', 'taken'] as const).map((s) =>
        h('span', {}, h('i', { class: `cell ${s}` }), { free: 'free', mine: 'yours', 'mine-soft': 'yours, soft', shared: 'shared seat', soft: 'someone\'s soft hold', taken: 'taken' }[s]))));

  const act = async (at: At, cell: Cell, alternate: boolean) => {
    if (table.classList.contains('busy')) return;
    if (!drawnFor) {
      notices.show({ type: 'about:blank', title: 'No occupier', status: 400, detail: 'choose who you are acting as; declare one under Occupiers if there is none' });
      return;
    }
    const who = drawnFor;
    const kind = clickAct(cell.state, alternate);
    const body = { who, ...at, slots: [cell.slot] };
    if (kind === 'claim' && state.explain) {
      const answer = await api.explain(world.name, body);
      if (answer.ok) notices.ok(`The door would admit ${who} at ${atLabel(at)} slot ${cell.slot}. Nothing was written.`);
      else notices.show(answer.problem);
      return;
    }
    const answer = kind === 'claim'
      ? await api.claim(world.name, state.soft ? { ...body, soft: true, until: Date.now() + state.softMinutes * 60_000 } : body)
      : kind === 'release' ? await api.release(world.name, body)
        : kind === 'confirm' ? await api.confirm(world.name, body)
          : await api.giveUp(world.name, body);
    if (!answer.ok) {
      notices.show(answer.problem);
      return;
    }
    const r = answer.value;
    const displaced = r.displaced?.length ? ` · displaced ${r.displaced.map((d) => `${d.occupier} (${runs(d.slots)})`).join(', ')}` : '';
    notices.ok(`${kind} ${runs(r.slots ?? [cell.slot])} at ${atLabel(at)} for ${who} · commit ${r.commitSeq} · head ${short(answer.head ?? r.head)}${displaced}`);
    await refresh();
  };

  const draw = async () => {
    table.classList.add('busy');
    const occ = await api.occupiers(world.name);
    const occupiers = notices.take(occ);
    if (!occupiers) {
      table.classList.remove('busy');
      return;
    }
    // Copies of other Worlds (hands) are not held here; they are only read.
    const local = occupiers.occupiers.filter((o: Occupier) => o.kind !== 'foreign');
    if (state.actor === null || !local.some((o) => o.name === state.actor)) state.actor = local.find((o) => !o.keeper)?.name ?? local[0]?.name ?? null;
    mount(actor, ...local.map((o) => h('option', { value: o.name, selected: o.name === state.actor }, `${o.name}${o.keeper ? ' (the World)' : ''} · ${o.kind}`)));

    const rows = rowsFor(world.preset, state.from);
    const read = await Promise.all(rows.map(async (row) => {
      const [offered, ...holdings] = await Promise.all([
        api.offered(world.name, row.at),
        ...local.map((o) => api.holdings(world.name, o.name, row.at)),
      ]);
      if (!offered!.ok) return { row, problem: offered!.problem, cells: [] as Cell[] };
      const hs = holdings.flatMap((a) => (a.ok ? [a.value as Holding] : []));
      return { row, problem: null, cells: cellsOf(world.leafSlotBits, offered!.value.slots, hs, state.actor) };
    }));
    const failed = read.find((r) => r.problem);
    if (failed?.problem) notices.show(failed.problem);

    const cols = Array.from({ length: world.leafSlotBits }, (_, s) => s);
    drawnFor = state.actor;
    table.classList.remove('busy');
    mount(table, h('table', { class: `grid bits-${world.leafSlotBits}` },
      h('thead', {}, h('tr', {}, h('th', {}, ''), ...cols.map((s) => h('th', { class: 'mono' }, world.leafSlotBits > 24 && s % 5 !== 0 ? '' : String(s))))),
      h('tbody', {}, ...read.map(({ row, cells }) => h('tr', {},
        h('th', { class: 'mono row-label' }, row.label),
        ...cells.map((cell) => h('td', {},
          h('button', {
            class: `cell ${cell.state}`,
            title: [`${atLabel(row.at)} · slot ${cell.slot}`, cell.holders.length ? `held by ${cell.holders.join(', ')}` : '', cell.soft.length ? `soft: ${cell.soft.join(', ')}` : '']
              .filter(Boolean).join(' · '),
            'aria-label': `${atLabel(row.at)} slot ${cell.slot}: ${cell.state}`,
            onclick: (e: Event) => void act(row.at, cell, (e as MouseEvent).shiftKey || (e as MouseEvent).altKey),
          }))))))));
  };

  return draw;
};

// -- occupiers --------------------------------------------------------------------------------

const occupiersTab = (el: HTMLElement, api: Api, world: World, notices: Notices, refresh: () => Promise<void>) => {
  const list = h('div', { class: 'card' });
  const name = h('input', { type: 'text', required: true, placeholder: 'alice' });
  const kind = h('select', {}, ...['holder', 'event', 'series'].map((k) => h('option', { value: k }, k)));
  const holder = h('select', { disabled: true });
  const rank = h('input', { type: 'number', value: 0, class: 'short' });
  const pinned = h('input', { type: 'checkbox' });
  const seats = h('input', { type: 'number', min: world.seats, max: 1024, value: world.seats, class: 'short' });
  kind.addEventListener('change', () => { holder.disabled = kind.value === 'holder'; });

  const declare = async (e: Event) => {
    e.preventDefault();
    const made = notices.take(await api.declare(world.name, {
      name: name.value.trim(),
      kind: kind.value as 'holder' | 'event' | 'series',
      ...(kind.value === 'holder' ? {} : { holder: holder.value }),
      rank: Number(rank.value) || 0,
      pinned: pinned.checked,
    }));
    if (!made) return;
    notices.ok(`Declared ${made.occupier.kind} ${made.occupier.name} (id ${made.occupier.id})`);
    name.value = '';
    await refresh();
  };

  const grow = async (e: Event) => {
    e.preventDefault();
    const answer = notices.take(await api.capacity(world.name, Number(seats.value)));
    if (!answer) return;
    notices.ok(`Seats: ${answer.seats}`);
    await refresh();
  };

  mount(el, h('div', { class: 'columns' },
    list,
    h('div', { class: 'stack' },
      h('form', { class: 'card', onsubmit: declare },
        h('h3', {}, 'Declare an occupier'),
        field('Name', name),
        field('Kind', kind, 'a holder holds in its own name; an event or series in a holder\'s'),
        field('Holder', holder, 'for an event or series'),
        field('Rank', rank, 'higher outranks lower, when displacing is allowed'),
        field('Pinned', pinned, 'never displaced'),
        h('button', { class: 'primary', type: 'submit' }, 'Declare')),
      h('form', { class: 'card', onsubmit: grow },
        h('h3', {}, 'Capacity'),
        field('Seats', seats, 'how many may hold one slot at once; 1–1024, only grows'),
        h('button', { type: 'submit' }, 'Set seats')))));

  return async () => {
    const answer = notices.take(await api.occupiers(world.name));
    if (!answer) return;
    const holders = answer.occupiers.filter((o) => o.kind === 'holder');
    mount(holder, ...holders.map((o) => h('option', { value: o.name }, o.name)));
    mount(list, h('table', {},
      h('thead', {}, h('tr', {}, ...['Id', 'Name', 'Kind', 'Holder', 'Rank', 'Pinned'].map((t) => h('th', {}, t)))),
      h('tbody', {}, ...answer.occupiers.map((o) => h('tr', {},
        h('td', { class: 'mono muted' }, o.id),
        h('td', {}, o.name, o.keeper ? h('span', { class: 'tag', title: 'the World itself: its busy time is what other Worlds see' }, 'keeper') : null),
        h('td', {}, o.kind === 'foreign' ? h('span', { class: 'tag', title: 'a copy of another World, taken in for relays' }, 'hand') : o.kind),
        h('td', {}, o.holder ?? ''),
        h('td', { class: 'mono' }, o.rank),
        h('td', {}, o.pinned ? 'yes' : ''))))));
  };
};

// -- the journal ------------------------------------------------------------------------------

const PAGE = 100;

const journalTab = (el: HTMLElement, api: Api, world: World, notices: Notices) => {
  const timeline = h('ol', { class: 'timeline', reversed: true });
  const chain = h('span', { class: 'chain' });
  const more = h('button', { class: 'small' }, 'Older');
  let commits: Commit[] = [];

  const entryLine = (e: Commit['entries'][number]) => {
    const set = maskSlots(e.set);
    const clear = maskSlots(e.clear);
    return h('li', {},
      h('span', { class: 'who' }, e.occupier), ' ',
      h('span', { class: `role r-${e.role}` }, e.role),
      h('span', { class: 'mono muted' }, ` @${e.key}`), ' ',
      set.length ? h('span', { class: 'set mono' }, `+${runs(set)}`) : null, ' ',
      clear.length ? h('span', { class: 'clear mono' }, `−${runs(clear)}`) : null);
  };

  /** Each commit seals the one before it: every prevHash must be the hash above it. */
  const checkChain = () => {
    const sorted = [...commits].sort((a, b) => a.seq - b.seq);
    const broken = sorted.find((c, i) => i > 0 && c.prevHash !== sorted[i - 1]!.hash);
    chain.className = `chain ${broken ? 'broken' : 'whole'}`;
    chain.textContent = broken ? `chain breaks at ${broken.seq}` : `${sorted.length} commits, chain whole`;
  };

  const draw = () => {
    mount(timeline, ...[...commits].sort((a, b) => b.seq - a.seq).map((c) => h('li', { class: 'commit' },
      h('div', { class: 'commit-head' },
        h('span', { class: 'seq mono' }, `#${c.seq}`),
        h('strong', {}, c.kind),
        c.actor ? h('span', {}, ` by ${c.actor}`) : null,
        c.ref && c.ref !== c.actor ? h('span', { class: 'muted' }, ` · ${c.ref}`) : null,
        h('span', { class: 'mono muted hash', title: `${c.prevHash} → ${c.hash}` }, `${short(c.prevHash, 6)} → ${short(c.hash, 6)}`)),
      c.entries.length ? h('ul', { class: 'entries' }, ...c.entries.map(entryLine)) : null)));
    checkChain();
    more.disabled = commits.length === 0 || Math.min(...commits.map((c) => c.seq)) <= 1;
  };

  more.addEventListener('click', async () => {
    const oldest = Math.min(...commits.map((c) => c.seq));
    const since = Math.max(0, oldest - 1 - PAGE);
    const page = notices.take(await api.journal(world.name, since, oldest - 1 - since));
    if (!page) return;
    commits = [...page.commits, ...commits];
    draw();
  });

  mount(el, h('div', { class: 'card' },
    h('div', { class: 'toolbar' }, h('h3', {}, 'Journal'), chain, more),
    h('p', { class: 'hint' }, 'Every act, sealed. + sets bits, − clears them; the key is the position the bits sit at.'),
    timeline));

  return async () => {
    // First the latest page; after that only what is new since the newest shown.
    const newest = commits.length ? Math.max(...commits.map((c) => c.seq)) : null;
    if (newest === null) {
      const top = notices.take(await api.journal(world.name, 0, 1));
      if (!top) return;
      const page = notices.take(await api.journal(world.name, Math.max(0, top.seq - PAGE), PAGE));
      if (!page) return;
      commits = page.commits;
    } else {
      const page = notices.take(await api.journal(world.name, newest, 1000));
      if (!page) return;
      commits = [...commits, ...page.commits];
    }
    draw();
  };
};

// -- the asks inbox ---------------------------------------------------------------------------

const VERDICTS: readonly [Verdict, string][] = [['accept', 'Accept'], ['reject', 'Reject'], ['takeBack', 'Take back'], ['moveTo', 'Move to…']];

const asksTab = (el: HTMLElement, api: Api, world: World, notices: Notices) => {
  const list = h('div', { class: 'card' });
  mount(el,
    h('p', { class: 'hint' }, 'Asks planners have made of this World. Accepting claims through this World\'s own door, so it can be refused too. Move to answers with other slots.'),
    list);

  const answer = async (ask: Ask, verdict: Verdict, slotsText: string) => {
    let slots: number[] | undefined;
    if (verdict === 'moveTo') {
      slots = parseSlots(slotsText) ?? undefined;
      if (!slots) {
        notices.show({ type: 'about:blank', title: 'Which slots?', status: 400, detail: 'give the slots to move to, like "14-15" or "9,10"' });
        return;
      }
    }
    const done = notices.take(await api.answer(world.name, ask.token, verdict, slots));
    if (!done) return;
    notices.ok(`${verdict} ${short(ask.token)} → ${String(done.answer.state ?? 'answered')}`);
  };

  return async () => {
    const asks = notices.take(await api.asks(world.name));
    if (!asks) return;
    if (asks.asks.length === 0) {
      mount(list, h('p', { class: 'hint' }, 'No asks. When a planner lays a relay with a leg on this World and asks, it arrives here.'));
      return;
    }
    mount(list, h('table', {},
      h('thead', {}, h('tr', {}, ...['Token', 'From', 'At', 'Slots', 'State', 'Held', 'Because', 'Answer'].map((t) => h('th', {}, t)))),
      h('tbody', {}, ...asks.asks.map((ask) => {
        const slots = h('input', { type: 'text', placeholder: 'slots', class: 'short mono', value: runs(ask.slots) });
        return h('tr', {},
          h('td', { class: 'mono', title: ask.token }, short(ask.token, 10)),
          h('td', {}, ask.from),
          h('td', { class: 'mono' }, atLabel(atOf(ask.parametric, ask.coordinates))),
          h('td', { class: 'mono' }, runs(ask.slots)),
          h('td', {}, standingBadge(ask.state ?? 'none')),
          h('td', { class: 'mono' }, runs(ask.held)),
          h('td', { class: 'muted' }, ask.because ? `${ask.because.by}` : ''),
          h('td', { class: 'actions' }, ...VERDICTS.map(([v, label]) =>
            h('button', { class: `small ${v === 'accept' ? 'primary' : v === 'reject' ? 'danger' : ''}`, onclick: () => void answer(ask, v, slots.value) }, label)), slots));
      }))));
  };
};
